import React from 'react';
import { Button } from '@/components/ui/button';
import { BookOpen, FileText, Download } from 'lucide-react';

const LessonPlans = () => {
  const lessonPlanCategories = [
    {
      title: 'Numeracy',
      description: 'Engaging math activities and number concepts for young learners',
      image: 'https://images.unsplash.com/photo-1758598737999-e5b659b3d65c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxjaGlsZHJlbiUyMGxlYXJuaW5nJTIwYWN0aXZpdGllc3xlbnwwfHx8fDE3NjEzOTU5MjB8MA&ixlib=rb-4.1.0&q=85',
      topics: ['Counting', 'Number Recognition', 'Basic Operations', 'Patterns', 'Shapes']
    },
    {
      title: 'Literacy',
      description: 'Reading, writing, and language development activities',
      image: 'https://images.unsplash.com/photo-1544772711-57da9c7368fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxjaGlsZHJlbiUyMGxlYXJuaW5nJTIwYWN0aXZpdGllc3xlbnwwfHx8fDE3NjEzOTU5MjB8MA&ixlib=rb-4.1.0&q=85',
      topics: ['Phonics', 'Letter Recognition', 'Story Time', 'Writing Skills', 'Vocabulary']
    },
    {
      title: 'Theme-Based Learning',
      description: 'Experiential learning through engaging themes and projects',
      image: 'https://images.pexels.com/photos/8033972/pexels-photo-8033972.jpeg',
      topics: ['Seasons', 'Animals', 'Community Helpers', 'Nature', 'Festivals']
    }
  ];

  return (
    <div className="lesson-plans-page">
      {/* Header */}
      <section className="py-20 bg-gradient-to-br from-[#f5f3f8] via-[#f5f3f8] to-[#f5f3f8]">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-800 mb-6" data-testid="lesson-plans-header">Lesson Plans & Resources</h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Ready-to-use lesson plans designed by experts to make your teaching more effective and engaging.
          </p>
        </div>
      </section>

      {/* Lesson Plan Categories */}
      <section className="py-20 bg-white" data-testid="lesson-plans-categories">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {lessonPlanCategories.map((category, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-[#f5f3f8] rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all transform hover:-translate-y-2" data-testid={`lesson-category-${index}`}>
                <img src={category.image} alt={category.title} className="w-full h-56 object-cover" />
                <div className="p-8 space-y-4">
                  <h3 className="text-2xl font-bold text-gray-800">{category.title}</h3>
                  <p className="text-gray-600">{category.description}</p>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700">Topics Covered:</p>
                    <div className="flex flex-wrap gap-2">
                      {category.topics.map((topic, topicIndex) => (
                        <span key={topicIndex} className="bg-[#f5f3f8] text-[#a855f7] px-3 py-1 rounded-full text-sm font-medium">
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Button 
                    data-testid={`view-plans-btn-${index}`}
                    className="w-full bg-[#a855f7] hover:bg-[#9333ea] text-white py-4 rounded-full mt-4"
                  >
                    View Plans <FileText className="ml-2" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-20 bg-gradient-to-br from-[#f5f3f8] to-[#f5f3f8]" data-testid="resources-section">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 text-center mb-12">Additional Resources</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <BookOpen className="w-12 h-12 text-[#a855f7] mb-4" />
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Activity Templates</h3>
                <p className="text-gray-600 mb-4">Customizable templates for various learning activities and assessments</p>
                <Button variant="outline" className="border-2 border-[#a855f7] text-[#a855f7] hover:bg-[#f5f3f8] rounded-full">
                  Browse Templates <Download className="ml-2" />
                </Button>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <FileText className="w-12 h-12 text-[#14b8a6] mb-4" />
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Assessment Tools</h3>
                <p className="text-gray-600 mb-4">Developmental checklists and observation forms for tracking progress</p>
                <Button variant="outline" className="border-2 border-[#14b8a6] text-[#14b8a6] hover:bg-[#f5f3f8] rounded-full">
                  Get Tools <Download className="ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LessonPlans;